/* tslint:disable: member-ordering*/
import { of as observableOf, Observable } from 'rxjs';

import {
  share,
  exhaustMap,
  concatMap,
  catchError,
  mergeMap,
  map,
  withLatestFrom,
  first,
  tap,
  filter,
  switchMap
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { StocksMarketAction } from './stocks-market.action';
import { ErrorsType } from '../../enum/errors.type';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { BackendService } from '../../services/backend/backend.service';
import { StockTrackingChartInterval } from '../../components/charts/stock-tracking-chart/stock-tracking-chart-interval';
import { RoutesTable } from '../../resources/routes-table.model';
import { RealtimeHub } from '../../enum/realtime/realtime-hub.enum';
import { StocksMarketState } from './stocks-market.reducer';
import { RealtimeService } from '../../services/realtime/realtime.service';
import { RealtimeQuoteService } from '../../services/realtime/realtime-quote.service';
import { UserManagerService } from '../../services/users/user-manager.service';
import { Roles } from '../../models/roles/roles.enum';

@Injectable()
export class StocksMarketEffects {
  constructor(
    private actions$: Actions<StocksMarketAction>,
    private store: Store<AppState>,
    private backendService: BackendService,
    private realtimeService: RealtimeService,
    private quoteApiService: RealtimeQuoteService
  ) { 
    this.subscribeQuoteApi();
  }

  /**
   * Subscribe QuoteApi subject
   **/
  private subscribeQuoteApi() {
    this.quoteApiService.quoteApiSubjectObs().subscribe(update => {
        this.onIncomingData({
            type: StocksMarketAction.getQuoteSuccess,
            payload: update
        });
    });
  }

  /**
   * Abertura do mercado
   **/

  @Effect({ dispatch: false })
  marketClosing$ = this.actions$
  .pipe(
    ofType(StocksMarketAction.marketClosed)
  );

  /**
   * Encerramento (fechado) do mercado
   **/
  @Effect({ dispatch: false })
  marketOpening$ = this.actions$
  .pipe(
    ofType(StocksMarketAction.marketOpen)
  );

  /**
   * Status do mercado (fechado ou aberto)
   *
   **/
  @Effect({ dispatch: false })
  getMarketStatus$ = this.actions$
  .pipe(
    ofType(StocksMarketAction.getMarketStatus),
    withLatestFrom(this.store.select(s => s.stocksExchange.stocksMarket)),
    first(),
    tap(([action, market]) => {
      this.realtimeService
        .subscribeToMarketStatus()
        .map(market => market.IsOpen)
        .distinctUntilChanged()
        .subscribe(isOpen => {
          const action = isOpen
            ? { type: StocksMarketAction.marketOpen }
            : { type: StocksMarketAction.marketClosed };
          this.onIncomingData(action);
        });
    })
  );

  /**
   * Tendencia para ativo/ativos
   * */
  @Effect({ dispatch: false })
  getTrends$ = this.actions$
  .pipe(
    ofType(StocksMarketAction.getTrends),
    filter(
      action =>
        this.realtimeService.registerSubscribersAndCheckIfSubscriptionIsNeeded(
          RealtimeHub.Tendency,
          action.payload
        ) == true
    ),
    map(action => {
      //subscribe to unique symbol tendency
      if (typeof action.payload == 'string') {
        return this.realtimeService
          .subscribeToTendency(action.payload)
          .subscribe(tendency => {
            this.onIncomingData({
              type: StocksMarketAction.getTrendsSuccess,
              payload: tendency
            });
          });
      }
      //subscribe by array
      if (Array.isArray(action.payload)) {
        return this.realtimeService
          .subscribeArrayTendency(action.payload)
          .subscribe(tendency => {
            this.onIncomingData({
              type: StocksMarketAction.getTrendsSuccess,
              payload: tendency
            });
          });
      }
    }),
    share()
  );

  /**
   * Cancela inscrições de tendência a partir de um array de ativos
   */
  @Effect({ dispatch: false })
  unsubscribeTrends$ = this.actions$
  .pipe(
      ofType(StocksMarketAction.unsubscribeTrends),
      map((action: any) => {
        this.realtimeService
          .unsubscribeArrayTendency(action.payload)
          .subscribe(result => {
            if (result) {
              this.store.dispatch({
                type: StocksMarketAction.unsubscribeTrendsSuccess,
                payload: action.payload
              });
            } else {
              this.store.dispatch({
                type: StocksMarketAction.unsubscribeTrendsError,
                payload: action.payload
              });
            }
          });
      })
    );

  /**
   * Cotação de ativo/ativos
   */
  @Effect({ dispatch: false })
  getQuote$ = this.actions$.pipe(
    ofType(StocksMarketAction.getQuote),
    map(action => {
      //subscribe to unique symbol quote
      const { role } = UserManagerService.getPreviousUserData();
      if(role == Roles.Activated) {
        if (typeof action.payload == 'string')
          this.quoteApiService.subscribeActiveQuote(action.payload);
        else
          this.quoteApiService.subscribeActivesArrayQuote(action.payload);
      }
      else{
        if (typeof action.payload == 'string')
          this.realtimeService.subscribeActiveQuote(action.payload)
          .subscribe(update => {
            this.onIncomingData({
              type: StocksMarketAction.getQuoteSuccess,
              payload: update
            });
          });
        else
          this.realtimeService.subscribeActivesArrayQuote(action.payload)
          .subscribe(update => {
            this.onIncomingData({
              type: StocksMarketAction.getQuoteSuccess,
              payload: update
            });
          });
      }
    }),
    share()
  );

  /** Para a cotação de ativos de ativos */
  @Effect({ dispatch: false })
  unsubscribeQuote$ = this.actions$
  .pipe(
    ofType(StocksMarketAction.unsubscribeQuotes),
    map((action: any) => {
      this.quoteApiService.unsubscribeActiveArrayQuote(action.payload);
    })
  );

  @Effect()
  public getInstrument$ = this.actions$
  .pipe(
    ofType(StocksMarketAction.getInstrumentInfo),
    withLatestFrom(this.store.select(s => s.stocksExchange.stocksMarket)),
    mergeMap(([action, market]) => {
      const normalizedStockId = action.payload.toUpperCase();

      if (market.instruments[normalizedStockId]) {
        return observableOf({
          type: StocksMarketAction.getInstrumentInfoSuccess,
          payload: market.instruments[normalizedStockId]
        });
      }

      // Request pendente
      if (market.pendingInstruments.find(i => i == normalizedStockId))
        return observableOf({
          type: StocksMarketAction.gettingInstrumentInfo,
          payload: normalizedStockId
        });

      // Buscando instrumento
      this.store.dispatch({
        type: StocksMarketAction.gettingInstrumentInfo,
        payload: normalizedStockId
      });
      const url = RoutesTable.composeUri(
        RoutesTable.quote.routes.getInstrumentInfo,
        normalizedStockId
      );
      return this.backendService.get<DefaultResponse>(url)
      .pipe(
        map(result => {
          if (result.ok)
            return {
              type: StocksMarketAction.getInstrumentInfoSuccess,
              payload: result.value
            };

          return {
            type: StocksMarketAction.getInstrumentInfoError,
            payload: {
              type: ErrorsType.Error,
              error: result.messages,
              code: normalizedStockId
            }
          };
        }),
        catchError(error =>
          observableOf({
            type: StocksMarketAction.getInstrumentInfoError,
            payload: { error: error, code: action.payload.toUpperCase() }
          })
        )
      );
    }),
    share()
  );

  @Effect()
  public getInstrumentByArray$ = this.actions$.pipe(
    ofType(StocksMarketAction.getInstrumentsInfoByArray),
    // .withLatestFrom(this.store.select(s => s), (action, state) => {return {action: action, market: state.stocksExchange.stocksMarket}})
    concatMap(action => {
      let market: StocksMarketState = null;
      this.store
        .select(s => s.stocksExchange.stocksMarket)
        .subscribe(data => (market = data));

      let normalizedStockId: Array<string> = action.payload
        .filter(symbol => symbol)
        .map(symbol => symbol.toUpperCase());

      const notAlredyHaveInstruments = normalizedStockId.filter(
        symbol => !market.instruments[symbol]
      );
      const alredyHaveInstruments = normalizedStockId.filter(
        symbol => market.instruments[symbol]
      );

      if (!notAlredyHaveInstruments.length) {
        return observableOf({
          type: StocksMarketAction.getInstrumentsInfoByArraySuccess,
          payload: []
        });
      } else if (alredyHaveInstruments.length) {
        this.store.dispatch({
          type: StocksMarketAction.getInstrumentsInfoByArraySuccess,
          payload: []
        });
      }

      const notGetting = notAlredyHaveInstruments.filter(
        symbol => market.pendingInstruments.find(item => item == symbol) == null
      );
      this.store.dispatch({
        type: StocksMarketAction.gettingInstrumentsArrayInfo,
        payload: notGetting
      });

      const filter = notAlredyHaveInstruments.reduce(
        (prev, current, index, array) =>
          prev +
          ` Code eq '${current}' ${index == array.length - 1 ? '' : 'or'}`,
        ''
      );

      const url = RoutesTable.composeUri(
        RoutesTable.quote.routes.getInstrumentsInfoByArray,
        filter
      );
      return this.backendService.get<DefaultResponse>(url)
      .pipe(
        map(result => {
          if (result.ok) {
            return {
              type: StocksMarketAction.getInstrumentsInfoByArraySuccess,
              payload: result.value
            };
          }

          return {
            type: StocksMarketAction.getInstrumentsInfoByArrayError,
            payload: [
              {
                type: ErrorsType.Error,
                error: result.messages,
                code: notGetting
              }
            ]
          };
        }),
        catchError(error =>
          observableOf({
            type: StocksMarketAction.getInstrumentsInfoByArrayError,
            payload: [{ error: error, code: action.payload }]
          })
        )
      );
    }),
    share()
  );

  /** 
     *  Para o snapshot do ativo
     */
  @Effect()
    public getQuoteInfo$ = this.actions$
        .pipe(
            ofType(StocksMarketAction.getQuoteInfo),
            withLatestFrom(
                this.store.select(s => s.stocksExchange.stocksMarket.quoteInfo)
            ),
            switchMap(([action, state]) => {
                const normalizedStockId = action.payload.toUpperCase();
                const url = RoutesTable.composeUri(
                    RoutesTable.quote.routes.getQuoteInfo,
                    normalizedStockId
                  );
                return this.backendService.get<DefaultResponse>(url)
                    .pipe(
                        map(result => {
                            if (result.ok)
                                return {
                                    type: StocksMarketAction.getQuoteInfoSuccess,
                                    payload: result.value
                                };
                            return {
                                type: StocksMarketAction.getQuoteInfoError,
                                payload: result.value
                            };
                        }),
                        catchError(error => {
                            return observableOf({
                                type: StocksMarketAction.getQuoteInfoError,
                                payload: error
                            });
                        })
                    );
            }),
            share()
        );

  /** Histórico da última hora */
  @Effect()
  public getLastHourHistory$ = this.actions$.pipe(
    ofType(StocksMarketAction.getLastHourHistory),
    withLatestFrom(
      this.store.select(s => s.stocksExchange.stocksMarket.quoteHistory)
    ),
    //.filter( ([action, state]) => !state[action.payload] || !!state[action.payload][StockTrackingChartInterval.OneHour])
    exhaustMap(([action, state]) => {
      action.payload = action.payload.toUpperCase();

      if (
        state[action.payload] &&
        state[action.payload][StockTrackingChartInterval.OneHour]
      )
        return observableOf({
          type: StocksMarketAction.getLastHourHistorySuccess,
          payload: {
            code: action.payload,
            interval: StockTrackingChartInterval.OneHour,
            data: state[action.payload][StockTrackingChartInterval.OneHour]
          }
        });

      return this.backendService
        .get<DefaultResponse>(
          RoutesTable.composeUri(
            RoutesTable.quote.routes.lastHourHistory,
            action.payload
          )
        )
        .pipe(
          map(result => {
            if (result.ok)
              return {
                type: StocksMarketAction.getLastHourHistorySuccess,
                payload: {
                  code: action.payload,
                  interval: StockTrackingChartInterval.OneHour,
                  data: result.value
                }
              };

            return {
              type: StocksMarketAction.getLastHourHistoryError,
              payload: result.value
            };
          }),
          catchError(error => {
            return observableOf({
              type: StocksMarketAction.getLastHourHistoryError,
              payload: error
            });
          })
        );
    }),
    share()
  );

  /** Histórico do último dia */
  @Effect()
  public getLastDayHistory$ = this.actions$.pipe(
    ofType(StocksMarketAction.getLastDayHistory),
    withLatestFrom(
      this.store.select(s => s.stocksExchange.stocksMarket.quoteHistory)
    ),
    //.filter( ([action, state]) => !state[action.payload] || !state[action.payload][StockTrackingChartInterval.OneDay])
    exhaustMap(([action, state]) => {
      action.payload = action.payload.toUpperCase();

      if (
        state[action.payload] &&
        state[action.payload][StockTrackingChartInterval.OneDay]
      )
        return observableOf({
          type: StocksMarketAction.getLastDayHistorySuccess,
          payload: {
            code: action.payload,
            interval: StockTrackingChartInterval.OneDay,
            data: state[action.payload][StockTrackingChartInterval.OneDay]
          }
        });

      return this.backendService
        .get<DefaultResponse>(
          RoutesTable.composeUri(
            RoutesTable.quote.routes.lastDayHistory,
            action.payload
          )
        )
        .pipe(
          map(result => {
            if (result.ok)
              return {
                type: StocksMarketAction.getLastDayHistorySuccess,
                payload: {
                  code: action.payload,
                  interval: StockTrackingChartInterval.OneDay,
                  data: result.value
                }
              };

            return {
              type: StocksMarketAction.getLastDayHistoryError,
              payload: result.value
            };
          }),
          catchError(error => {
            return observableOf({
              type: StocksMarketAction.getLastDayHistoryError,
              payload: error
            });
          })
        );
    }),
    share()
  );

  /** Histórico da última semana */
  @Effect()
  public getLastWeekHistory$ = this.actions$.pipe(
    ofType(StocksMarketAction.getLastWeekHistory),
    withLatestFrom(
      this.store.select(s => s.stocksExchange.stocksMarket.quoteHistory)
    ),
    //.filter( ([action, state]) => !state[action.payload] || !state[action.payload][StockTrackingChartInterval.OneWeek])
    exhaustMap(([action, state]) => {
      action.payload = action.payload.toUpperCase();

      if (
        state[action.payload] &&
        state[action.payload][StockTrackingChartInterval.OneWeek]
      )
        return observableOf({
          type: StocksMarketAction.getLastWeekHistorySuccess,
          payload: {
            code: action.payload,
            interval: StockTrackingChartInterval.OneWeek,
            data: state[action.payload][StockTrackingChartInterval.OneWeek]
          }
        });

      return this.backendService
        .get<DefaultResponse>(
          RoutesTable.composeUri(
            RoutesTable.quote.routes.lastWeekHistory,
            action.payload
          )
        )
        .pipe(
          map(result => {
            if (result.ok)
              return {
                type: StocksMarketAction.getLastWeekHistorySuccess,
                payload: {
                  code: action.payload,
                  interval: StockTrackingChartInterval.OneWeek,
                  data: result.value
                }
              };

            return {
              type: StocksMarketAction.getLastWeekHistoryError,
              payload: result.value
            };
          }),
          catchError(error => {
            return observableOf({
              type: StocksMarketAction.getLastWeekHistoryError,
              payload: error
            });
          })
        );
    }),
    share()
  );

  /** Histórico do último mês */
  @Effect()
  public getLastMonthHistory$ = this.actions$.pipe(
    ofType(StocksMarketAction.getLastMonthHistory),
    withLatestFrom(
      this.store.select(s => s.stocksExchange.stocksMarket.quoteHistory)
    ),
    //.filter( ([action, state]) => !state[action.payload] || !state[action.payload][StockTrackingChartInterval.OneMonth])
    exhaustMap(([action, state]) => {
      action.payload = action.payload.toUpperCase();

      if (
        state[action.payload] &&
        state[action.payload][StockTrackingChartInterval.OneMonth]
      )
        return observableOf({
          type: StocksMarketAction.getLastMonthHistorySuccess,
          payload: {
            code: action.payload,
            interval: StockTrackingChartInterval.OneMonth,
            data: state[action.payload][StockTrackingChartInterval.OneMonth]
          }
        });

      return this.backendService
        .get<DefaultResponse>(
          RoutesTable.composeUri(
            RoutesTable.quote.routes.lastMonthHistory,
            action.payload
          )
        )
        .pipe(
          map(result => {
            if (result.ok)
              return {
                type: StocksMarketAction.getLastMonthHistorySuccess,
                payload: {
                  code: action.payload,
                  interval: StockTrackingChartInterval.OneMonth,
                  data: result.value
                }
              };

            return {
              type: StocksMarketAction.getLastMonthHistoryError,
              payload: result.value
            };
          }),
          catchError(error => {
            return observableOf({
              type: StocksMarketAction.getLastMonthHistoryError,
              payload: error
            });
          })
        );
    }),
    share()
  );

  /** Histórico do último ano*/
  @Effect()
  public getLastYearHistory$ = this.actions$.pipe(
    ofType(StocksMarketAction.getLastYearHistory),
    withLatestFrom(
      this.store.select(s => s.stocksExchange.stocksMarket.quoteHistory)
    ),
    //.filter( ([action, state]) => !state[action.payload] || !state[action.payload][StockTrackingChartInterval.OneYear])
    exhaustMap(([action, state]) => {
      action.payload = action.payload.toUpperCase();

      if (
        state[action.payload] &&
        state[action.payload][StockTrackingChartInterval.OneYear]
      )
        return observableOf({
          type: StocksMarketAction.getLastYearHistorySuccess,
          payload: {
            code: action.payload,
            interval: StockTrackingChartInterval.OneYear,
            data: state[action.payload][StockTrackingChartInterval.OneYear]
          }
        });

      return this.backendService
        .get<DefaultResponse>(
          RoutesTable.composeUri(
            RoutesTable.quote.routes.lastYearHistory,
            action.payload
          )
        )
        .pipe(
          map(result => {
            if (result.ok)
              return {
                type: StocksMarketAction.getLastYearHistorySuccess,
                payload: {
                  code: action.payload,
                  interval: StockTrackingChartInterval.OneYear,
                  data: result.value
                }
              };

            return {
              type: StocksMarketAction.getLastYearHistoryError,
              payload: result.value
            };
          }),
          catchError(error => {
            return observableOf({
              type: StocksMarketAction.getLastYearHistoryError,
              payload: error
            });
          })
        );
    }),
    share()
  );

  /** Histórico dos últimos 5 ano*/
  @Effect()
  public getLastFiveYearsHistory$ = this.actions$.pipe(
    ofType(StocksMarketAction.getLastFiveYearsHistory),
    withLatestFrom(
      this.store.select(s => s.stocksExchange.stocksMarket.quoteHistory)
    ),
    //.filter( ([action, state]) => !state[action.payload] || !state[action.payload][StockTrackingChartInterval.FiveYears])
    exhaustMap(([action, state]) => {
      action.payload = action.payload.toUpperCase();

      if (
        state[action.payload] &&
        state[action.payload][StockTrackingChartInterval.FiveYears]
      )
        return observableOf({
          type: StocksMarketAction.getLastFiveYearsHistorySuccess,
          payload: {
            code: action.payload,
            interval: StockTrackingChartInterval.FiveYears,
            data: state[action.payload][StockTrackingChartInterval.FiveYears]
          }
        });

      return this.backendService
        .get<DefaultResponse>(
          RoutesTable.composeUri(
            RoutesTable.quote.routes.lastFiveYearsHistory,
            action.payload
          )
        )
        .pipe(
          map(result => {
            if (result.ok) {
              let points = [];
              for (let i = result.value.length - 1; i >= 0; i--) {
                if (i == 0 || (i + 1) % 5 == 0) {
                  points.unshift(result.value[i]);
                }
              }
              return {
                type: StocksMarketAction.getLastFiveYearsHistorySuccess,
                payload: {
                  code: action.payload,
                  interval: StockTrackingChartInterval.FiveYears,
                  data: points
                }
              };
            }

            return {
              type: StocksMarketAction.getLastFiveYearsHistoryError,
              payload: result.value
            };
          }),
          catchError(error => {
            return observableOf({
              type: StocksMarketAction.getLastFiveYearsHistoryError,
              payload: error
            });
          })
        );
    }),
    share()
  );

    /** 
     * Histórico de cotação de determinado ativo 
     */
    @Effect()
    public getQuoteHistory$ = this.actions$
        .pipe(
            ofType(StocksMarketAction.getQuoteHistory),
            withLatestFrom(
                this.store.select(s => s.stocksExchange.stocksMarket.quoteHistory)
            ),
            switchMap(([action, state]) => {
                const url: string = `quotehistory/${action.payload.interval}/${action.payload.symbol.toUpperCase()}/${action.payload.top}`;
                return this.backendService.get<DefaultResponse>(url)
                    .pipe(
                        map(result => {
                            if (result.ok)
                                return {
                                    type: StocksMarketAction.getQuoteHistorySuccess,
                                    payload: {
                                        code: action.payload.symbol,
                                        interval: action.payload.codeInterval,//Verificar novos intervalos 1m, 15m e 1D
                                        data: result.value
                                    }
                                };

                            return {
                                type: StocksMarketAction.getQuoteHistoryError,
                                payload: result.value
                            };
                        }),
                        catchError(error => {
                            return observableOf({
                                type: StocksMarketAction.getQuoteHistoryError,
                                payload: error
                            });
                        })
                    );
            }),
            share()
        );

  /** Erro ao buscar dados do histórico para o gráfico */
  @Effect({ dispatch: false })
  chartDataRetrievingError$ = this.actions$
  .pipe(
    ofType(
      StocksMarketAction.getLastHourHistoryError,
      StocksMarketAction.getLastDayHistoryError,
      StocksMarketAction.getLastWeekHistoryError,
      StocksMarketAction.getLastMonthHistoryError,
      StocksMarketAction.getLastTwoMonthsHistoryError,
      StocksMarketAction.getLastYearHistoryError,
      StocksMarketAction.getLastFiveYearsHistoryError
    )
  );

  private onIncomingData(action: StocksMarketAction) {
    this.store.dispatch(action);
  }
}
